<template>
  <div class="import-trucks">
    <p class="hint">
      {{ $t("fleetTrucks.import_hint") }}
    </p>

    <div class="actions-row">
      <vs-button flat @click="DownloadTruckTemplate()">
        <i class="fal fa-file-download"></i>&nbsp;{{ $t("fleetTrucks.download_template") }}
      </vs-button>
      <input ref="file" type="file" accept=".xlsx,.xls" :disabled="busy" @change="OnFileChange">
    </div>

    <div class="actions-row">
      <vs-button :disabled="!file || busy" :loading="busy && !applied" @click="Run(true)">
        <i class="fal fa-search"></i>&nbsp;{{ $t("fleetTrucks.preview") }}
      </vs-button>
      <vs-button success :disabled="!canApply" :loading="busy && applied" @click="Run(false)">
        <i class="fal fa-save"></i>&nbsp;{{ $t("fleetTrucks.apply_save") }}
      </vs-button>
    </div>

    <div v-if="report" class="report">
      <p class="summary">
        <b>{{ report.dry_run ? $t("fleetTrucks.report_dry_run") : $t("fleetTrucks.report_saved") }}:</b>
        {{ $t("fleetTrucks.summary_create", { n: report.summary.create }) }} ·
        {{ $t("fleetTrucks.summary_update", { n: report.summary.update }) }} ·
        {{ $t("fleetTrucks.summary_unchanged", { n: report.summary.unchanged }) }} ·
        <span :class="{bad: report.summary.error}">{{ $t("fleetTrucks.summary_error", { n: report.summary.error }) }}</span> ·
        <span :class="{bad: report.summary.driver_errors}">{{ $t("fleetTrucks.summary_driver_errors", { n: report.summary.driver_errors }) }}</span>
      </p>
      <table class="table is-fullwidth is-narrow">
        <thead>
        <tr>
          <th>{{ $t("fleetTrucks.col_row") }}</th>
          <th>{{ $t("fleetTrucks.col_plate") }}</th>
          <th>{{ $t("fleetTrucks.col_result") }}</th>
          <th>{{ $t("fleetTrucks.col_changes") }}</th>
          <th>{{ $t("fleetTrucks.col_drivers") }}</th>
          <th>{{ $t("fleetTrucks.col_problems") }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="row in report.rows" :key="row.row" :class="{'row-error': row.action === 'error'}">
          <td>{{ row.row }}</td>
          <td>{{ row.plate_number }}</td>
          <td>{{ ActionLabel(row.action) }}</td>
          <td>{{ row.changes.join(', ') }}</td>
          <td>{{ row.drivers_linked === null ? '—' : row.drivers_linked }}</td>
          <td class="problems">{{ row.errors.concat(row.driver_errors).join(' · ') }}</td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import MixinFleetTrucks from '@/Mixins/Fleet/Trucks';

/**
 * Bulk truck / default-crew upload (TMS report #7). Always previews first
 * (dry_run) — the save button only unlocks for the same file after a clean-ish
 * preview, so an operator sees exactly what will change.
 */
export default {
  mixins: [MixinFleetTrucks],
  data: () => ({
    file: null,
    report: null,
    // The exact File the current report describes — saving is allowed only
    // for that same file, so a preview can never unlock a different upload.
    reportFile: null,
    requestId: 0,
    busy: false,
    applied: false,
  }),
  computed: {
    canApply() {
      return !this.busy && !!this.file && !!this.report && this.report.dry_run
          && this.reportFile === this.file
          && (this.report.summary.create + this.report.summary.update + this.report.summary.unchanged) > 0;
    },
  },
  methods: {
    OnFileChange(event) {
      this.file = (event.target.files && event.target.files[0]) || null;
      this.report = null;
      this.reportFile = null;
      this.requestId += 1; // any in-flight response now belongs to an old file
    },
    ActionLabel(action) {
      return {
        create: this.$t('fleetTrucks.action_create'),
        update: this.$t('fleetTrucks.action_update'),
        unchanged: this.$t('fleetTrucks.action_unchanged'),
        error: this.$t('fleetTrucks.action_error'),
      }[action] || action;
    },
    Run(dryRun) {
      // One request at a time: a preview started during a save would
      // invalidate (and drop) the save's result and the list refresh.
      if (!this.file || this.busy) return;
      // Saving requires a preview of THIS file (the button is disabled otherwise).
      if (!dryRun && !this.canApply) return;
      const file = this.file;
      const requestId = ++this.requestId;
      this.busy = true;
      this.applied = !dryRun;
      this.ImportTrucks(file, dryRun).then((report) => {
        if (requestId !== this.requestId || file !== this.file) return; // stale response
        this.report = report;
        this.reportFile = file;
        this.busy = false;
        if (!dryRun) {
          this.$emit('success');
        }
      }).catch(() => {
        if (requestId === this.requestId) {
          this.busy = false;
        }
      });
    },
  },
};
</script>

<style scoped>
.import-trucks {
  min-width: 640px;
}

.hint {
  font-size: 13px;
  color: #555;
  margin-bottom: 10px;
}

.actions-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.summary .bad {
  color: crimson;
  font-weight: bold;
}

.report {
  max-height: 50vh;
  overflow: auto;
}

.row-error td {
  background: #fff1f1;
}

.problems {
  color: crimson;
  font-size: 12px;
}
</style>
